// Minimap pings (M3): transient rings on tiles where something happened —
// `attack:*` events, milestone reaches, champion summons — plus direct pushes
// from event-log clicks. Ring lifetime and colours live in @aod/ui
// (`drawPings`); this module maps the event stream to pings, dedups by the
// stream's monotonic event id, and ages them out.
//
// Visibility rule (minimap-design.md §5): a ping is only emitted when the tile
// is not fog for the local player at ping time.

import type { GameSnapshot } from '@aod/protocol';
import { type MinimapPing, minimapPingMs as PING_MS, minimapTileKey } from '@aod/ui';
import { useCallback, useMemo, useRef, useState } from 'react';

export interface PingTarget {
  readonly q: number;
  readonly r: number;
}

/** City node id → board coords + owner (for `attack:*` payloads). */
export function cityPositions(
  snapshot: GameSnapshot | null,
): Map<string, PingTarget & { ownerId: number }> {
  const map = new Map<string, PingTarget & { ownerId: number }>();
  for (const n of snapshot?.scene.children ?? []) {
    if (n.type !== 'city') continue;
    const q = n.props?.q as number | undefined;
    const r = n.props?.r as number | undefined;
    if (q === undefined || r === undefined || n.name === undefined) continue;
    map.set(n.name, { q, r, ownerId: (n.props?.ownerId as number | undefined) ?? 0 });
  }
  return map;
}

/** World position (axial_to_world output) → containing hex. Inverse of `x = √3(q + r/2)`, `z = 1.5r`. */
export function worldToAxial(x: number, z: number): PingTarget {
  const r = z / 1.5;
  const q = x / Math.sqrt(3) - r / 2;
  return { q: Math.round(q), r: Math.round(r) };
}

/** City id from the payload (attacks) or the event source (summon_started), if any. */
function payloadCityId(event: { type: string; payload?: Record<string, unknown> }): string | null {
  const id = event.payload?.targetCityId as string | undefined;
  if (id) return id;
  if (event.type === 'legend:summon_started') {
    const src = (event.payload?.source ?? (event as { source?: string }).source) as
      | string
      | undefined;
    return src ?? null;
  }
  return null;
}

/**
 * Where an event pings, or null when it carries no resolvable position.
 * Attacks/summons point at their city; milestones at their `position`.
 */
export function eventToPingTarget(
  event: { id: number; type: string; payload?: Record<string, unknown> },
  cities: Map<string, PingTarget & { ownerId: number }>,
): PingTarget | null {
  const cityId = payloadCityId(event);
  if (cityId) {
    const city = cities.get(cityId);
    return city ? { q: city.q, r: city.r } : null;
  }
  if (event.type === 'milestone:reached') {
    const pos = event.payload?.position as readonly number[] | undefined;
    const x = pos?.[0];
    const z = pos?.[2];
    if (x !== undefined && z !== undefined) return worldToAxial(x, z);
  }
  return null;
}

/**
 * Severity: attacks on the local player's cities ring major, other visible
 * attacks minor, everything else info.
 */
export function pingSeverity(
  event: { type: string; payload?: Record<string, unknown> },
  cities: Map<string, PingTarget & { ownerId: number }>,
  localPlayer: number,
): MinimapPing['severity'] {
  const cityId = event.payload?.targetCityId as string | undefined;
  const own = cityId !== undefined && cities.get(cityId)?.ownerId === localPlayer;
  if (event.type.startsWith('attack:')) return own ? 'major' : 'minor';
  return 'info';
}

/** Event types that produce pings. */
const PINGABLE = /^(attack:|milestone:reached$|legend:summon_started$)/;

/** True when the tile exists and is not fog in the minimap snapshot. */
function tileVisible(
  minimapSnapshot: { tiles: ReadonlyArray<{ q: number; r: number; visibility: string }> } | null,
  q: number,
  r: number,
): boolean {
  if (!minimapSnapshot) return false;
  const key = minimapTileKey(q, r);
  return minimapSnapshot.tiles.some(
    (t) => minimapTileKey(t.q, t.r) === key && t.visibility !== 'fog',
  );
}

/**
 * Pings derived from the sim event stream plus direct pushes (event-log
 * clicks). Ingestion dedups by the stream's monotonic event id (the 400-event
 * window replays on every publish, so a plain derive would re-ping); the
 * rolling list survives the publishes it takes a ring to play out and prunes
 * past PING_MS.
 */
export function useMinimapPings(
  events: ReadonlyArray<{ id: number; type: string; payload?: Record<string, unknown> }>,
  snapshot: GameSnapshot | null,
  minimapSnapshot: { tiles: ReadonlyArray<{ q: number; r: number; visibility: string }> } | null,
  localPlayer: number,
): {
  pings: MinimapPing[];
  pushPing: (target: PingTarget, severity?: MinimapPing['severity']) => void;
} {
  const consumed = useRef(new Set<number>());
  const [pushed, setPushed] = useState<MinimapPing[]>([]);

  const pushPing = useCallback((target: PingTarget, severity: MinimapPing['severity'] = 'info') => {
    setPushed((prev) => [...prev.slice(-8), makePing(target, severity)]);
  }, []);

  const pings = useMemo(() => {
    const cities = cityPositions(snapshot);
    const now = performance.now();
    const kept = pushed.filter((p) => now - p.startedAt < PING_MS + 50);
    const fresh: MinimapPing[] = [];
    for (const event of events.slice(-40)) {
      if (consumed.current.has(event.id) || !PINGABLE.test(event.type)) continue;
      const target = eventToPingTarget(event, cities);
      if (!target || !tileVisible(minimapSnapshot, target.q, target.r)) continue;
      consumed.current.add(event.id);
      fresh.push({
        ...target,
        startedAt: now,
        severity: pingSeverity(event, cities, localPlayer),
      });
    }
    if (consumed.current.size > 512) consumed.current = new Set([...consumed.current].slice(-256));
    return [...kept, ...fresh].slice(-24);
  }, [events, snapshot, minimapSnapshot, localPlayer, pushed]);

  return { pings, pushPing };
}

/** A direct ping (event-log clicks): the caller supplies the coords. */
function makePing(target: PingTarget, severity: MinimapPing['severity']): MinimapPing {
  return { ...target, startedAt: performance.now(), severity };
}
